import {
  parseMcpOAuthCallbackMessage,
  type McpOAuthCallbackMessage,
} from './mcp-oauth-callback.helpers';

export type McpOAuthPopupResult = 'success' | 'error' | null;

export function getMcpOAuthPopupResult(popup: Window): McpOAuthPopupResult {
  try {
    const result = new URL(popup.location.href).searchParams.get(
      'mcp_oauth_result',
    );
    return result === 'success' || result === 'error' ? result : null;
  } catch {
    return null;
  }
}

type McpOAuthGatewayTerminalPopupResult = {
  result: 'success' | 'error';
  state?: string;
  error?: string;
};

function readMcpOAuthGatewayTerminalResultFromPopup(
  popup: Window,
): McpOAuthGatewayTerminalPopupResult | null {
  try {
    const url = new URL(popup.location.href);
    const result = url.searchParams.get('mcp_oauth_result');
    if (result !== 'success' && result !== 'error') {
      return null;
    }
    return {
      result,
      state: url.searchParams.get('state') ?? undefined,
      error: url.searchParams.get('error') ?? undefined,
    };
  } catch {
    return null;
  }
}

function matchesExpectedMcpOAuthStateNonce(
  message: McpOAuthCallbackMessage,
  expectedStateNonce: string | null | undefined,
): boolean {
  if (!expectedStateNonce) {
    return true;
  }
  if (!message.state) {
    return false;
  }
  return parseMcpOAuthStateNonce(message.state) === expectedStateNonce;
}

type WaitForMcpOAuthPopupAuthorizationInput = {
  popup: Window;
  authorizationFailedMessage: string;
  authorizationTimedOutMessage: string;
  authorizationWindowClosedMessage: string;
  timeoutMs?: number;
};

export type McpOAuthCallbackPayload = {
  code?: string;
  state?: string;
  exchangedViaGateway?: boolean;
};

export function parseMcpOAuthStateNonce(
  state: string | null | undefined,
): string | null {
  if (!state) {
    return null;
  }
  const [nonce] = state.split('.');
  return nonce || null;
}

export function readMcpOAuthCallbackFromPopup(
  popup: Window,
  expectedStateNonce?: string | null,
): McpOAuthCallbackPayload | null {
  try {
    if (popup.closed) {
      return null;
    }
    const url = new URL(popup.location.href);
    if (url.searchParams.has('mcp_oauth_result')) {
      return null;
    }
    const code = url.searchParams.get('code');
    const state = url.searchParams.get('state');
    if (!code || !state) {
      return null;
    }
    if (expectedStateNonce) {
      const messageNonce = parseMcpOAuthStateNonce(state);
      if (messageNonce !== expectedStateNonce) {
        return null;
      }
    }
    return { code, state };
  } catch {
    return null;
  }
}

type McpOAuthPopupWaitHandlers = {
  onSuccess: (message: McpOAuthCallbackMessage) => void;
  onError: (error: Error) => void;
};

type SubscribeToMcpOAuthPopupResultInput =
  WaitForMcpOAuthPopupAuthorizationInput & {
    handlers: McpOAuthPopupWaitHandlers;
    expectedStateNonce?: string | null;
    listenToBroadcastChannel?: boolean;
  };

function isMcpOAuthPopupMessageEvent(
  event: MessageEvent,
  popup: Window,
): boolean {
  if (event.source === popup) {
    return true;
  }
  return event.source === null && event.origin === window.location.origin;
}

function subscribeToMcpOAuthPopupResult({
  popup,
  authorizationFailedMessage,
  authorizationTimedOutMessage,
  authorizationWindowClosedMessage,
  timeoutMs,
  handlers,
  expectedStateNonce,
  listenToBroadcastChannel = true,
}: SubscribeToMcpOAuthPopupResultInput): () => void {
  let resultChannel: BroadcastChannel | null = null;
  let closeGuardTimeout: number | null = null;
  let completed = false;

  const clearCloseGuard = () => {
    if (closeGuardTimeout === null) {
      return;
    }
    window.clearTimeout(closeGuardTimeout);
    closeGuardTimeout = null;
  };

  const finish = () => {
    completed = true;
    window.clearTimeout(timeout);
    window.clearInterval(closeCheck);
    clearCloseGuard();
    window.removeEventListener('message', handleMessage);
    resultChannel?.close();
  };

  const handleOnce = (message: McpOAuthCallbackMessage) => {
    if (completed) {
      return;
    }
    finish();
    if (!popup.closed) {
      popup.close();
    }
    if (message.type === 'MCP_OAUTH_SUCCESS') {
      handlers.onSuccess(message);
      return;
    }
    handlers.onError(
      new Error(
        message.type === 'MCP_OAUTH_ERROR'
          ? message.error
          : authorizationFailedMessage,
      ),
    );
  };

  const handleOAuthResult = (data: unknown) => {
    const message = parseMcpOAuthCallbackMessage(data);
    if (!message) {
      return;
    }
    if (!matchesExpectedMcpOAuthStateNonce(message, expectedStateNonce)) {
      return;
    }
    handleOnce(message);
  };

  const timeout = window.setTimeout(() => {
    if (completed) {
      return;
    }
    finish();
    handlers.onError(new Error(authorizationTimedOutMessage));
  }, timeoutMs ?? 120_000);

  const armCloseGuard = () => {
    if (closeGuardTimeout !== null) {
      return;
    }
    closeGuardTimeout = window.setTimeout(() => {
      if (completed) {
        return;
      }
      if (!popup.closed) {
        closeGuardTimeout = null;
        return;
      }
      finish();
      handlers.onError(new Error(authorizationWindowClosedMessage));
    }, 1000);
  };

  const closeCheck = window.setInterval(() => {
    const terminalResult = readMcpOAuthGatewayTerminalResultFromPopup(popup);
    if (terminalResult?.result === 'success') {
      handleOAuthResult({
        type: 'MCP_OAUTH_SUCCESS',
        state: terminalResult.state,
      });
      return;
    }
    if (terminalResult?.result === 'error') {
      handleOAuthResult({
        type: 'MCP_OAUTH_ERROR',
        error: terminalResult.error ?? authorizationFailedMessage,
        state: terminalResult.state,
      });
      return;
    }
    if (popup.closed) {
      armCloseGuard();
      return;
    }
    clearCloseGuard();
  }, 500);

  function handleMessage(event: MessageEvent) {
    if (!isMcpOAuthPopupMessageEvent(event, popup)) {
      return;
    }
    handleOAuthResult(event.data);
  }

  if (listenToBroadcastChannel) {
    resultChannel = new BroadcastChannel('terra-mcp-oauth');
    resultChannel.onmessage = (event: MessageEvent) => {
      handleOAuthResult(event.data);
    };
  }
  window.addEventListener('message', handleMessage);

  return finish;
}

export function openBlankMcpOAuthPopup(): Window {
  const popup = window.open(
    'about:blank',
    `mcp-oauth-${Date.now()}`,
    'width=600,height=700,scrollbars=yes',
  );
  if (!popup) {
    throw new Error('popup_blocked');
  }
  return popup;
}

export function navigateMcpOAuthPopup({
  popup,
  authorizeUrl,
}: {
  popup: Window;
  authorizeUrl: string;
}): void {
  popup.location.href = authorizeUrl;
}

export function openMcpOAuthPopup(authorizeUrl: string): Window {
  const popup = openBlankMcpOAuthPopup();
  navigateMcpOAuthPopup({ popup, authorizeUrl });
  return popup;
}

export function waitForMcpOAuthPopupCallback({
  popup,
  authorizationFailedMessage,
  authorizationTimedOutMessage,
  authorizationWindowClosedMessage,
  timeoutMs = 120_000,
  expectedStateNonce,
}: WaitForMcpOAuthPopupAuthorizationInput & {
  expectedStateNonce?: string | null;
}): Promise<McpOAuthCallbackPayload> {
  return new Promise<McpOAuthCallbackPayload>((resolve, reject) => {
    subscribeToMcpOAuthPopupResult({
      popup,
      authorizationFailedMessage,
      authorizationTimedOutMessage,
      authorizationWindowClosedMessage,
      timeoutMs,
      expectedStateNonce,
      handlers: {
        onSuccess: (message) => {
          const code =
            message.type === 'MCP_OAUTH_SUCCESS' ? message.code : undefined;
          const state =
            message.type === 'MCP_OAUTH_SUCCESS' ? message.state : undefined;
          resolve({
            code,
            state,
            exchangedViaGateway: message.type === 'MCP_OAUTH_SUCCESS' && !code,
          });
        },
        onError: reject,
      },
    });
  });
}

export function waitForMcpOAuthPopupAuthorization({
  popup,
  authorizationFailedMessage,
  authorizationTimedOutMessage,
  authorizationWindowClosedMessage,
  timeoutMs = 120_000,
}: WaitForMcpOAuthPopupAuthorizationInput): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    subscribeToMcpOAuthPopupResult({
      popup,
      authorizationFailedMessage,
      authorizationTimedOutMessage,
      authorizationWindowClosedMessage,
      timeoutMs,
      handlers: {
        onSuccess: () => {
          resolve();
        },
        onError: reject,
      },
    });
  });
}
